import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { Component, computed, inject, input, type InputSignal } from '@angular/core';
import { type Params, RouterModule } from '@angular/router';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';

import type { IEntityInfo } from '@core/models/app.model';
import { NotifyService } from '@core/services/notifyService.service';
import { getCountryFromGoogleAddress } from '@shared/forms/helpers';
import { UmeFlagComponent } from '@widgets/ume-flag/ume-flag.component';

@Component({
  selector: 'ume-company-link',
  imports: [RouterModule, NgxFloatUiModule, CdkCopyToClipboard, UmeFlagComponent],
  template: `
    <div
      class="ume-company-link"
      [floatUi]="companyPopover"
      [showTrigger]="popTriggers.hover"
      [showDelay]="100"
      [hideTimeout]="hideTimeout()"
      [appendTo]="'body'"
      [placement]="placement()">
      @if (showCountryFlag()) {
        <ume-flag [code]="companyCountryCode()"></ume-flag>
      }
      @if (brId()) {
        <a
          [routerLink]="['/company/my-network/br', brId(), 'controlled']"
          [queryParams]="linkParams()"
          [queryParamsHandling]="queryParamsHandling()">
          {{ company().companyName }}
        </a>
      }
      @if (link()) {
        <a
          [routerLink]="link()"
          [target]="target()"
          [queryParams]="linkParams()"
          [queryParamsHandling]="queryParamsHandling()">
          {{ company().companyName }}
        </a>
      }
      @if (!brId() && !link()) {
        <span>{{ company().companyName }}</span>
      }
    </div>
    <float-ui-content #companyPopover>
      <div style="padding: 3px 0;">
        <ume-flag [code]="companyCountryCode()"></ume-flag>
        {{ company().country.name }}
      </div>
      <button
        type="button"
        class="copy-text"
        [cdkCopyToClipboard]="company().companyName"
        (click)="copiedNotification('Entity name copied!')"
        style="padding: 3px 0;">
        {{ company().companyName }}
      </button>
    </float-ui-content>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        max-width: 100%;
      }

      .ume-company-link {
        position: relative;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        vertical-align: middle;
        text-overflow: ellipsis;
        padding: 3px 0;
      }

      .ume-company-link ume-flag {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }

      .ume-company-link > span,
      .ume-company-link > a {
        padding-left: 22px;
        vertical-align: middle;
      }

      a {
        text-decoration: none;
      }

      .copy-text {
        cursor: copy;
        border: none;
        background: none;
        padding: 0;
        font: inherit;
        color: inherit;
        text-align: left;
      }
    `,
  ],
})
export class UmeCompanyLinkComponent {
  readonly popTriggers = NgxFloatUiTriggers;

  private readonly notify: NotifyService = inject(NotifyService);

  company = input.required<IEntityInfo>();
  link = input<string | string[]>();
  brId = input<string>();
  linkParams = input<Params>({} as Params);
  queryParamsHandling = input<'merge' | 'preserve' | ''>('merge');
  placement = input<NgxFloatUiPlacements>(NgxFloatUiPlacements.RIGHT);
  hideTimeout: InputSignal<number> = input(100);
  showCountryFlag = input<boolean>(true);
  target = input<'_blank' | '_self'>('_self');

  companyCountryCode = computed(() => {
    const company: IEntityInfo = this.company();

    return (
      company.country?.code ||
      (getCountryFromGoogleAddress(company.googleAddress)?.short_name as string)
    );
  });

  public copiedNotification(message: string): void {
    this.notify.notifyInfo(message, 1000);
  }
}
